import React, { PropsWithChildren, useEffect, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useCurrentWalletAdapter from '../hooks/useCurrentWalletAdapter';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import { useDebounce } from 'react-use';
import useUserAccountPubKey from '../hooks/useUserAccountPubKey';
import useSweepstakesStore from 'src/stores/useSweepstakesStore';
import { EventSubscriber } from '@drift-labs/competitions-sdk';

const INITIAL_CONTEXT_VALUE: {
	ready: boolean;
	eventSubscriber: EventSubscriber;
} = { ready: false, eventSubscriber: undefined };

export const SweepstakesEventSubscriberContext = React.createContext<{
	ready: boolean;
	eventSubscriber?: EventSubscriber;
}>({ ready: false, eventSubscriber: undefined });

const SweepStakesEventSubscriberProvider = (props: PropsWithChildren<any>) => {
	const connection = useDriftStore((s) => s.connection.current);
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const currentAuthority = useCurrentWalletAdapter().publicKey;
	const currentUserAccount = useUserAccountPubKey();
	const walletIsConnected = useWalletIsConnected();
	const pollingMultiplier = useDriftStore((s) => s.pollingMultiplier);

	const sweepstakesClient = useSweepstakesStore((s) => s.competitionsClient);

	const [contextValue, setContextValue] = useState(INITIAL_CONTEXT_VALUE);

	const createEventSubscriber = () => {
		const eventSubscriber = new EventSubscriber(
			connection,
			sweepstakesClient.program,
			{
				maxTx: 128,
				maxEventsPerType: 256,
				orderBy: 'blockchain',
				commitment: 'confirmed',
				logProviderConfig: {
					type: 'websocket',
				},
				eventTypes: [
					'CompetitionRoundWinnerRecord',
					'CompetitionRoundSummaryRecord',
				],
			}
		);
		return eventSubscriber;
	};

	// The params that subscribing depend on can change in bursts, but we don't want to subscribe+unsubscribe in quick bursts because it's unnecessary. For this reason we're going to make a ticker to sync with these params, and only update it with a debounced hook.
	const [subscribeUpdateTicker, setSubscribeUpdateTicker] = useState([]);

	// The debounced hook which can only update the ticker at a maximum speed
	useDebounce(
		() => {
			setSubscribeUpdateTicker([]);
		},
		1000,
		[
			connection,
			driftClient,
			walletIsConnected && currentAuthority,
			pollingMultiplier,
			currentUserAccount,
		]
	);

	useEffect(() => {
		if (!connection) return;
		if (!driftClient) return;
		if (!driftClient.isSubscribed) return;
		if (!sweepstakesClient) return;

		console.log(`SweepStakesEventSubscriberProvider setup`);

		const eventSubscriber = createEventSubscriber();

		setContextValue({
			ready: true,
			eventSubscriber,
		});

		eventSubscriber.subscribe();

		return () => {
			eventSubscriber.unsubscribe();
		};
	}, [subscribeUpdateTicker]);

	return (
		<SweepstakesEventSubscriberContext.Provider value={contextValue}>
			{props.children}
		</SweepstakesEventSubscriberContext.Provider>
	);
};

export default SweepStakesEventSubscriberProvider;
